import { faMoneyBillTransfer } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormField } from "../../components/ui/form-field";
import { InputGroup, InputGroupText } from "../../components/ui/input-group";
import { toast } from "../../components/ui/use-toast";
import { useFormatNow } from "../../hooks/date";
import {
  initValidation,
  validate,
  findErrors,
  isEmpty,
  isNotEqualToField,
  isNotExactLength,
  isNotMoney,
  isNotTicket,
} from "../../hooks/formValidation";
import { CustomerData, emptyCustomerData } from "../../model/CustomerData";
import { emptyExchangeData, ExchangeData } from "../../model/ExchangeData";
import { CurrentSessionData } from "../../hooks/userSession";
import { authFetch } from "../../rest/authFetch";
import RestrictedAccess from "../../components/RestrictedAccess";

const Exchange = ({
  currentSession,
}: {
  currentSession: CurrentSessionData;
}) => {

  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData);
  const [exchange, setExchange] = useState(emptyExchangeData);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [validation, setValidation] = useState(
    initValidation("amount", "confirmation", "ticketNum")
  );
  const [amount, setAmount] = useState(0);
  const [value, setValue] = useState(0);

  const params = useParams();
  const navigate = useNavigate();
  const formatNow = useFormatNow();
  const customerUrl = useMemo(
    () => `${process.env.REACT_APP_API_BASE_URL}/customer/${params.id}`,
    [params.id]
  );

  useEffect(() => {
    authFetch(customerUrl)
      .then((response) => response.json())
      .then((result) => {
        setCustomer({
          ...emptyCustomerData,
          id: result.data.id,
          phone: result.data.phone,
          name: result.data.customer_name,
        });
        setAmount(Number(result.data.amount));
        setValue(Number(result.data.value));
        setExchange((e) => ({ ...e, customerId: result.data.id }));
      });
  }, [customerUrl]);

  const save = () => {
    setDisableSubmit(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(exchange),
    };
    authFetch(`${process.env.REACT_APP_API_BASE_URL}/exchange/save`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (!result.success) {
          setDisableSubmit(false);
        }
        toast({
          variant: result.success ? "default" : "destructive",
          title: "Mensaje",
          description: result.message,
        });
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isError = updateValidation(exchange);
    if (!isError) {
      save();
    }
  };

  const setField = (field: string, event: any) => {
    const updatedExchange = { ...exchange, [field]: event.target.value.trim() };
    setExchange(updatedExchange);
    updateValidation(updatedExchange, field);
  };

  const updateValidation = (exchange: ExchangeData, field = "") => {
    const newValidation = findErrors(
      exchange,
      validate(
        "amount",
        isEmpty("Debe capturar el monto del pago"),
        isNotMoney("El monto debe ser un número")
      ),
      validate(
        "confirmation",
        isEmpty("Debe confirmar el monto del pago"),
        isNotMoney("El monto debe ser un número"),
        isNotEqualToField("amount", "Los montos deben ser iguales")
      ),
      validate(
        "ticketNum",
        isEmpty("Debe capturar el número de ticket de pago"),
        isNotExactLength(
          4,
          "El número de ticket debe tener 4 letras o números"
        ),
        isNotTicket("El número de ticket debe contener solo letras o números")
      )
    );
    const isValidField =
      !!newValidation.fields[field] && !!validation.fields[field];
    setValidation(
      isValidField
        ? {
            ...validation,
            fields: {
              ...validation.fields,
              [field]: newValidation.fields[field],
            },
          }
        : newValidation
    );
    return newValidation.isError;
  };

  return (
    <RestrictedAccess currentSession={currentSession}>
      <div className="space-y-4">
        <div>
          <h5 className="text-lg font-semibold">
            <FontAwesomeIcon icon={faMoneyBillTransfer} /> &nbsp; Pagar con
            puntos
          </h5>
          <hr className="my-2" />
        </div>
        <div>
          El Cliente <strong>{customer.name}</strong>{" "}
          <small>({customer.phone})</small> tiene{" "}
          <strong>${value.toFixed(2)}</strong> disponibles{" "}
          <small>({amount.toFixed(2)} puntos)</small>.
        </div>
        <form noValidate onSubmit={handleSubmit} className="space-y-4">
          <FormField>
            <Label htmlFor="amount">
              Registrar pago con puntos por un monto de:
            </Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                $
              </InputGroupText>
              <Input
                id="amount"
                type="text"
                placeholder="###.##"
                value={exchange.amount}
                aria-invalid={validation.fields["amount"].isError}
                className={
                  validation.fields["amount"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("amount", event)}
              />
            </InputGroup>
            {validation.fields["amount"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["amount"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="confirmation">Confirmar monto:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                $
              </InputGroupText>
              <Input
                id="confirmation"
                type="text"
                placeholder="###.##"
                value={exchange.confirmation}
                aria-invalid={validation.fields["confirmation"].isError}
                className={
                  validation.fields["confirmation"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("confirmation", event)}
              />
            </InputGroup>
            {validation.fields["confirmation"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["confirmation"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="ticketNum">Número de ticket:</Label>
            <Input
              id="ticketNum"
              type="text"
              maxLength={4}
              placeholder="WXYZ"
              value={exchange.ticketNum}
              aria-invalid={validation.fields["ticketNum"].isError}
              className={
                validation.fields["ticketNum"].isError
                  ? "border-destructive focus-visible:ring-destructive"
                  : undefined
              }
              onChange={(event) => setField("ticketNum", event)}
            />
            {validation.fields["ticketNum"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["ticketNum"].message}
              </p>
            )}
          </FormField>
          <div>
            Registrado el <strong>{formatNow()}</strong> por{" "}
            <strong>admin</strong>.
          </div>
          <hr className="my-2" />
          <div className="flex justify-center gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate("/")}>
              Regresar
            </Button>
            <Button type="submit" disabled={disableSubmit}>
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </RestrictedAccess>
  );
};

export default Exchange;
